import type { SlipLadder } from "@/components/optimizer/props-slip-breakevens";

const ESTIMATE_URL = "https://api.underdogfantasy.com/v3/entry_slips/estimate";
const PICKEM_EXPERIENCE = "b34dfd93-d0e8-4da3-8bf4-45c15c548dec";

export const UNDERDOG_PICKEM_STATES = {
  TX: "16fa6ed3-ea21-4654-bcee-fb32d2f31357",
  GA: "c5c5c52c-485c-49a8-89fa-f81f6d29e215",
  MN: "a341aa09-5e4d-4e09-9112-16ff5c1d3fd2",
  OK: "a7cefa3f-2e83-49a6-9446-9d4cac363c56",
  UT: "03982b49-088a-4dd2-909a-a6065bb909c1",
  WI: "6929555b-155f-4eb3-a1ba-fc1b1c29be88",
} as const;

export type UnderdogPicks = { readonly options: readonly string[]; readonly key: string };

export type UnderdogPrice = { readonly multiplier: number; readonly flex: readonly SlipLadder[] };

export type UnderdogEstimate = UnderdogPrice | { readonly refusal: string };

export type UnderdogQuote = UnderdogEstimate & { readonly key: string };

export function underdogPicks(picks: readonly (string | null)[]): UnderdogPicks | null {
  const options = [...new Set(picks.filter((pick): pick is string => pick !== null && pick !== ""))].sort();
  if (options.length < 2 || options.length !== picks.length) return null;
  return { options, key: options.join(",") };
}

type FlexTier = { readonly losses?: unknown; readonly odds?: { readonly decimal?: unknown } | null };

function flexLadder(pricing: unknown, legs: number): SlipLadder | null {
  if (!pricing || typeof pricing !== "object") return null;
  const ladder: Record<number, number> = {};
  for (const tier of Object.values(pricing as Record<string, FlexTier>)) {
    const losses = Number(tier?.losses);
    const payout = Number(tier?.odds?.decimal);
    if (!Number.isInteger(losses) || losses < 0 || losses >= legs || !Number.isFinite(payout) || payout <= 0) return null;
    ladder[legs - losses] = payout;
  }
  return ladder[legs] === undefined ? null : ladder;
}

function parseFlex(flex: unknown, legs: number): SlipLadder[] {
  const offerings = (flex as { offerings?: unknown } | null)?.offerings;
  if (!Array.isArray(offerings)) return [];
  return offerings.flatMap((offering: { pricing?: unknown } | null) => {
    const ladder = flexLadder(offering?.pricing, legs);
    return ladder === null ? [] : [ladder];
  });
}

export function parseEstimate(body: unknown, legs: number): UnderdogEstimate | null {
  const value = body as { status?: unknown; odds?: { multiplier?: unknown } | null; flex?: unknown; errors?: unknown } | null;
  const multiplier = Number(value?.odds?.multiplier);
  if (value?.status === "valid" && Number.isFinite(multiplier) && multiplier > 1) return { multiplier, flex: parseFlex(value.flex, legs) };
  const errors = Array.isArray(value?.errors) ? (value.errors as { detail?: unknown }[]) : [];
  const detail = errors.map((error) => error?.detail).find((text): text is string => typeof text === "string" && text !== "");
  return detail === undefined ? null : { refusal: detail };
}

export async function fetchUnderdogQuote(picks: UnderdogPicks, signal: AbortSignal, random: () => number = Math.random): Promise<UnderdogQuote> {
  const states = Object.values(UNDERDOG_PICKEM_STATES);
  const query = new URLSearchParams({
    product: "fantasy",
    product_experience_id: PICKEM_EXPERIENCE,
    state_config_id: states[Math.floor(random() * states.length)],
  });
  picks.options.forEach((id, index) => {
    query.append(`options[${index}][id]`, id);
    query.append(`options[${index}][type]`, "OverUnderOption");
  });
  const response = await fetch(`${ESTIMATE_URL}?${query}`, { signal, credentials: "omit" });
  if (!response.ok) throw new Error(`Underdog answered ${response.status}`);
  const estimate = parseEstimate(await response.json(), picks.options.length);
  if (estimate === null) throw new Error("Underdog sent no price");
  return { ...estimate, key: picks.key };
}
